import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SearchIcon from '@mui/icons-material/Search';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import { IDocument, IFolder } from 'interfaces';
import { useTranslation } from 'react-i18next';
import { TenantPage } from 'components/common';
import { fmDocumentService } from 'services/api/documents/FMDocumentService';
import { fmFolderService } from 'services/api/documents/FMFolderService';
import { AddDocumentDialog } from 'components/common/documentLM/AddDocumentDialog';
import toast from 'features/toast';
import ConfirmationButtonDialog from 'components/helpers/ConfirmationButtonDelete';
import FolderIcon from '@mui/icons-material/Folder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import Divider from '@mui/material/Divider';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { isTechnician } from 'components/helpers/IsTechnician';
import { isTenantAdministrator } from 'components/helpers/IsTenantAdministrator';
import { isDirector } from 'components/helpers/IsDirector';
import ConfirmationButtonDelete from 'components/helpers/ConfirmationButtonDelete';
import UploadDocuments from './UploadDocuments';

export const DocumentsList = () => {
  const translationPrefix = 'pages.document';
  const { t } = useTranslation();

  const isDisableEdit = isTechnician() || isDirector();
  const canDelete = isTenantAdministrator();

  const [documents, setDocuments] = useState<IDocument[]>([]);
  const [advancedSearchList, setAdvancedSearchList] = useState<IDocument[]>([]);
  const [folders, setFolders] = useState<IFolder[]>([]);
  const [currentFolder, setCurrentFolder] = useState<IFolder>({
    id: 1,
    name: '/',
    description: '',
    parent: null
  });

  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  const [rootFolder, setRootFolder] = useState<IFolder | null>(null);
  const [allFolders, setAllFolders] = useState<IFolder[]>([]);

  useEffect(() => {
    fmFolderService.getAllPaginated().then((res) => {
      const root = res.find((f) => f.parent === null);
      setRootFolder(root || null);
      setCurrentFolder(root || null);
      setAllFolders(res); // 👈 salvo tutte le cartelle
    });
  }, []);

  const startingPopolateDocuments = () => {
    if (!currentFolder) return;

    setLoading(true);

    // Documenti con "folder = currentFolder.name"
    fmDocumentService
      .getAllPaginated(0, 0, { folder_exact: currentFolder.name })
      .then((response) => setDocuments(response.results));

    // Cartelle con parent = currentFolder.id
    fmFolderService.getAllPaginated(0, 0, { parent_id: currentFolder.id }).then((res) => {
      setFolders(res);
    });

    setLoading(false);
  };

  useEffect(startingPopolateDocuments, [currentFolder]);

  const handleGoBack = () => {
    if (!currentFolder) return;

    if (currentFolder.parent === null) {
      // siamo alla root
      setCurrentFolder(rootFolder);
    } else {
      const parentFolder = allFolders.find((f) => f.id === currentFolder.parent);
      setCurrentFolder(parentFolder || rootFolder);
    }
  };

  const handleDeleteDocument = (documentId: number) => {
    fmDocumentService.delete(documentId).then((res) => {
      if (res.status === 204) {
        toast.success(t('actions.successfully.deleted'));
      } else {
        toast.error(t('actions.successfully.error-generic'));
      }
      startingPopolateDocuments();
    });
  };

  const onSubmitNewDocument = (values) => {
    //upload document to API and handle response
    fmDocumentService
      .uploadDocument(
        values.tipology,
        values.attachment[0],
        values.name,
        values.description || '',
        '',
        '',
        values.category || '',
        values.supplier,
        values.warehouse
      )
      .then((res) => {
        if (res) {
          // if response is successful, show success toast message
          toast.success(t('entities.document.upload-success'));
          startingPopolateDocuments();
        } else {
          // if response is unsuccessful, show error toast message
          toast.error(t('entities.document.upload-error'));
        }
      });
  };

  const advanceSearch = () => {
    fmDocumentService.getAdvancedSearchDocuments(0, 0, { filter: filter }).then((res) => {
      if (res) setAdvancedSearchList(res);
    });
  };

  const getIcon = (filename) => {
    const _filename = (filename ?? '').toLowerCase();

    if (_filename.endsWith('.pdf')) {
      return <PictureAsPdfIcon fontSize="large" color="error" />;
    } else if (
      _filename.endsWith('.doc') ||
      _filename.endsWith('.docx') ||
      _filename.endsWith('.otd') ||
      _filename.endsWith('.pages')
    ) {
      return <TextSnippetIcon fontSize="large" color="info" />;
    } else if (_filename.endsWith('.png') || _filename.endsWith('.jpg')) {
      return <ImageIcon fontSize="large" color="info" />;
    } else {
      return <TextSnippetIcon fontSize="large" />;
    }
  };

  // -------------------------------------------------------- DELETE FOLDER
  const handlerOnDelete = (folder: IFolder) => {
    if (folder.id === 1) {
      toast.error(t('pages.document.cannot-delete-root-folder'));
      return;
    }

    fmFolderService.delete(folder.id).then((res) => {
      if (res.status === 204) {
        toast.success(t('actions.successfully.deleted'));
        if (currentFolder && currentFolder.id === folder.id) {
          setCurrentFolder(rootFolder);
        } else {
          startingPopolateDocuments();
        }
      } else {
        toast.error(t('actions.successfully.error-generic'));
      }
    });
  };

  return (
    <>
      <TenantPage
        title={t(`${translationPrefix}.title`)}
        subtitle={t(`${translationPrefix}.subtitle`)}>
        <div
          className="flex flex-col"
          // Container object
        >
          <div className="flex mb-4 pb-2">
            <div className="flex justify-between items-center w-full">
              <div className="flex flex-row gap-2 justify-evenly">
                {/* BACK */}
                <Tooltip title={t('actions.back')}>
                  <IconButton
                    onClick={handleGoBack}
                    disabled={!currentFolder || currentFolder.parent === null}>
                    <ArrowBackIcon />
                  </IconButton>
                </Tooltip>

                {/* HOME */}
                <Tooltip title={t('global.documents')}>
                  <IconButton onClick={() => setCurrentFolder(rootFolder)}>
                    <HomeIcon />
                  </IconButton>
                </Tooltip>
              </div>

              {/* TITOLO */}
              <span className="text-xl">
                {currentFolder
                  ? t(currentFolder.name.split('/').pop() || '/')
                  : t('global.documents')}
              </span>

              <TextField
                variant="standard"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                className="w-1/2"
                value={filter}
                onChange={(e) => {
                  if (e.target.value === '') {
                    setAdvancedSearchList([]);
                  }
                  setFilter(e.target.value);
                }}
              />
              {/* ADVANCED SEARCH */}
              <Tooltip title={t(`${translationPrefix}.advanced-search`)}>
                <Button variant="contained" onClick={advanceSearch}>
                  <ManageSearchIcon />
                </Button>
              </Tooltip>

              {!isDisableEdit && (
                <div className="flex flex-row justify-center items-center gap-2">
                  {/* MULTI UPLOAD FOLDER */}
                  <UploadDocuments isDisabled={currentFolder && currentFolder.name !== '/'} />

                  <AddDocumentDialog
                    onSubmit={onSubmitNewDocument}
                    currentFolder={currentFolder.name}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-fr">
              {advancedSearchList.length > 0 &&
                advancedSearchList.map((document, i) => (
                  <div
                    key={i}
                    className="w-full h-full px-4 py-2 items-center gap-3 cursor-pointer hover:shadow-3xl hover:bg-sky-100 border-2 border-gray-200">
                    <Tooltip title={document.description}>
                      <div className="flex flex-row gap-4 items-center justify-between">
                        <div className="flex flex-row gap-2 items-center">
                          <div className="flex flex-col gap-1">{getIcon(document.attachment)}</div>
                          <div className="flex flex-col">
                            <span className="text-lg text-center">{document.name}</span>
                            <span className="text-base items-center">{document.category}</span>
                            <span className="text-base items-center">{document.tipology}</span>
                          </div>
                        </div>
                        <div className="flex flex-row justify-items-end">
                          {/* QUI CI POTREI METTERE IL BOTTONE LINK FRA ENTITà E DOCUMENTO...MA MOOOLTO COMPLESSO */}
                          <IconButton
                            className="z-10"
                            color="info"
                            onClick={() => fmDocumentService.downloadAttachment(document.id)}>
                            <DownloadIcon />
                          </IconButton>
                          <div className="z-10">
                            {canDelete && (
                              <ConfirmationButtonDialog
                                id={document.id}
                                handleDelete={handleDeleteDocument}
                                iconButton
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </Tooltip>
                  </div>
                ))}
            </div>
            <Divider orientation="horizontal" />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-fr">
              {loading &&
                Array.from({ length: 24 }, (_, index) => index).map((el, i) => (
                  <Skeleton key={i} variant="rounded" className="w-full" height={64} />
                ))}
              {!loading &&
                folders.map((folder) => (
                  <div
                    key={folder.id}
                    className="relative border-2 border-gray-200 rounded-md overflow-hidden h-full">
                    {/* Pulsante delete in alto a destra */}
                    <div className="!absolute top-1 right-1 z-10 text-gray-500 hover:text-red-600">
                      {canDelete && (
                        <ConfirmationButtonDelete
                          id={folder.id}
                          iconButton={true}
                          handleDelete={() => {
                            handlerOnDelete(folder);
                          }}
                        />
                      )}
                    </div>

                    {/* Contenuto cliccabile del folder */}
                    <Button
                      className="w-full h-full px-4 py-2 flex items-center justify-start gap-3"
                      onClick={() => setCurrentFolder(folder)}>
                      <FolderIcon fontSize="large" />
                      <div className="flex flex-col text-left">
                        <div className="text-xl">{t(folder.name.split('/').pop())}</div>
                        {folder.description && (
                          <div className="text-sm font-bold">{folder.description}</div>
                        )}
                      </div>
                    </Button>
                  </div>
                ))}

              {!loading &&
                documents &&
                documents
                  .filter(
                    (d) =>
                      !filter ||
                      d.name.toLowerCase().includes(filter.toLowerCase()) ||
                      d.description.toLowerCase().includes(filter.toLowerCase())
                  )
                  .map((document, i) => (
                    <Card key={i} className="h-full flex flex-col border-2 border-gray-200">
                      <Tooltip title={document.description}>
                        <div className="w-full h-full py-2 cursor-pointer hover:shadow-3xl hover:bg-sky-100">
                          <CardContent
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'flex-start',
                              gap: 2,
                              overflow: 'hidden'
                            }}>
                            {/* Icona documento */}
                            <Box>{getIcon(document.attachment)}</Box>

                            {/* Testo */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  wordBreak: 'break-word'
                                }}>
                                {document.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {document.category}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {document.tipology}
                              </Typography>
                            </Box>

                            {/* Bottoni in colonna a destra */}
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: 1
                              }}>
                              <IconButton
                                color="info"
                                onClick={(e) => {
                                  e.stopPropagation(); // evita che clicchi anche il Card
                                  fmDocumentService.downloadAttachment(document.id);
                                }}>
                                <DownloadIcon />
                              </IconButton>

                              {canDelete && (
                                <ConfirmationButtonDialog
                                  id={document.id}
                                  handleDelete={handleDeleteDocument}
                                  iconButton
                                />
                              )}
                            </Box>
                          </CardContent>
                        </div>
                      </Tooltip>
                    </Card>
                  ))}
            </div>
          </div>
        </div>
      </TenantPage>
    </>
  );
};

export default DocumentsList;
